import type { BlockPick, StoredBlock } from '@icetus/contracts';

/**
 * 全屏编辑里点了一下页面，这一点该归谁。
 *
 * 页面上的东西分成两类，**保存路径完全不同**：
 *
 *   · 这一页的区块（`page_blocks`）—— PUT 整页替换，可以跨页不同；
 *   · 站点外壳（`site_blocks`）—— 页头 / 页脚 / 工具条，改一次全站生效。
 *
 * 搞错的后果不是报错，而是**改到了别的地方**：把外壳块当成页面块保存，
 * 页头会被写进 `page_blocks`，客户下次打开这一页就多出一段错位的页头；
 * 反过来把页面块交给外壳接口，块 id 根本不在那张表里。
 *
 * 所以这条判断单独抽出来、单独测。它只有三个出口，但每个出口都对应
 * 一句不同的话（打开抽屉 / 打开外壳抽屉 / 告诉客户为什么点不开）。
 */

/** 外壳块在主题版式里的实例 id 前缀。`shared-nav` / `shared-footer` 这一批 */
const SHELL_PREFIX = 'shared-';

export type PickResolution =
  { kind: 'page'; block: StoredBlock } | { kind: 'shell' } | { kind: 'none' };

/**
 * @param blocks 当前页的区块。**只传这一页的** —— 传全站的会把别的页的块认成
 *   这一页的，保存时写回错误的页面。
 */
export function resolvePick(blocks: StoredBlock[], pick: BlockPick): PickResolution {
  /*
   * 两条线索都要试：版式格子上挂的是实例 id（`home-hero`，主题写死的），
   * 客户自己加的块只有块 id。只认一条，另一类块就永远点不出来 ——
   * 而客户分不清"这一类不支持"，只会得出"点了没反应"。
   *
   * 页面优先于外壳：万一某个页面块真占了 `shared-` 前缀的实例 id，
   * 它确实在**这一页**里，按它自己那套改才对。
   */
  const found = blocks.find(
    (block) =>
      (pick.blockId !== null && block.id === pick.blockId) ||
      (pick.instanceId !== null && block.instanceId === pick.instanceId),
  );
  if (found) return { kind: 'page', block: found };
  if (pick.instanceId !== null && pick.instanceId.startsWith(SHELL_PREFIX)) {
    return { kind: 'shell' };
  }
  return { kind: 'none' };
}
